import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import clsx from 'clsx';

import { LinkedUser } from '../../../../api/user/types';
import { useStyles } from './style';
import { Input, InputAdornment } from '@material-ui/core';
import { ThemedButton } from '../../../../shared/components/themed-button';
import SearchIcon from '@material-ui/icons/Search';
import SvgProductsOrdered from '../../../../assets/icons/ProductsOrdered';
import { useImpersonateLinkedUserMutation } from '../../../../api/user';
import LinesEllipsis from 'react-lines-ellipsis';
import { OverlayModal } from '../../../../shared/components/overlay-modal';

interface Props {
  linkedRestaurants: LinkedUser[];
  linkedSuppliers: LinkedUser[];
  isOpen: boolean;
  setOpenModal: (value: boolean) => void;
  onClose: () => void;
}

const modalRoot = document.getElementById('root');

const SwitchUserModal: React.FC<Props> = ({linkedRestaurants, linkedSuppliers, isOpen, setOpenModal, onClose}) => {
  const classes = useStyles();
  const [selectedUser, setSelectedUser] = useState<LinkedUser | null>(null);
  const [search, setSearch] = useState("");
  const [tabIndex, setTabIndex] = useState(linkedRestaurants.length > 0 ? 0 : linkedSuppliers.length > 0 ? 1 : 0);
  const [impersonateLinkedUser]  = useImpersonateLinkedUserMutation();

  const handleTabChange = (newValue: number) => {
    setTabIndex(newValue);
    setSearch('');
    setSelectedUser(null);
  };

  const onApplyClick = () => {
    if (!selectedUser) return;
    const { id } = selectedUser;
    impersonateLinkedUser({ id });
    onClose();
  }

  if (!isOpen || !modalRoot) return null;

  return ReactDOM.createPortal(
    <OverlayModal closeOnClickOutside={true} showClose={true} boxClassName={classes.modalBox} isOpen={isOpen} onClose={() => setOpenModal(false)}>
      <div>
        <div className={classes.modalContent}>
          <h2>Switch User</h2>
          <p className={classes.subTitle}>Please select the user you want to switch to</p>

          <div className={classes.tabs}>
            {linkedRestaurants.length > 0 && <span onClick={() => handleTabChange(0)} className={clsx(classes.tab, tabIndex === 0 && classes.active)}>Venue</span>}
            {linkedSuppliers.length > 0 && <span onClick={() => handleTabChange(1)} className={clsx(classes.tab, tabIndex === 1 && classes.active)}>Supplier</span>}
          </div>

          <div className={classes.searchBox}>
            <Input
              classes={{
                root: classes.searchInput,
                underline: classes.noUnderline,
              }}
              placeholder={tabIndex === 0 ? 'Search Venue' : 'Search Supplier'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              startAdornment={
                <InputAdornment position='start'>
                  <SearchIcon className={classes.searchIcon} />
                </InputAdornment>
              }
            />
          </div>

          {tabIndex === 0 && <div className={classes.userList}>
            {
              linkedRestaurants
                .filter((user) => {
                  if( search === '') return true;
                  return user.location.toLowerCase().includes(search.toLowerCase()) ||
                    user.name.toLowerCase().includes(search.toLowerCase());
                }).length === 0 ? (
                  <div className={classes.noResultText}>No results</div>
                ) : (
                  linkedRestaurants
                    .filter((user) => {
                      if( search === '') return true;
                      return user.location.toLowerCase().includes(search.toLowerCase()) ||
                        user.name.toLowerCase().includes(search.toLowerCase());
                    })
                    .map((user) => (
                      <div
                        key={user.id}
                        className={clsx(classes.userCard, selectedUser?.id === user.id && classes.selected)}
                        onClick={() => setSelectedUser(user)}
                      >
                        <div className={classes.userInfo}>
                          <strong>{user.name}</strong>
                          <div className={'location'}><LinesEllipsis text={user.location || ''} ellipsis='...' /></div>
                        </div>
                      </div>
              )))}
          </div>}

          {tabIndex === 1 && <div className={classes.userList}>
            {
              linkedSuppliers
              .filter((user) => {
                if( search === '') return true;
                return user.location.toLowerCase().includes(search.toLowerCase()) ||
                  user.name.toLowerCase().includes(search.toLowerCase());
              }).length === 0 ? (
                <div className={classes.noResultText}>No results</div>
              ) : (
                linkedSuppliers
                  .filter((user) => {
                    if( search === '') return true;
                    return user.location.toLowerCase().includes(search.toLowerCase()) ||
                      user.name.toLowerCase().includes(search.toLowerCase());
                  })
                  .map((user) => (
                    <div
                      key={user.id}
                      className={clsx(classes.userCard, selectedUser?.id === user.id && classes.selected)}
                      onClick={() => setSelectedUser(user)}
                    >
                      <div className={classes.userInfo}>
                        <strong>{user.name}</strong>
                        <div className={'location'}><LinesEllipsis text={user.location || ''} ellipsis='...' /></div>
                      </div>
                      <div className={classes.logo}>
                        {
                          user.picture_url
                            ? <img src={user.picture_url} alt={user.name} />
                            : <SvgProductsOrdered width={28} height={28} />
                        }
                      </div>
                    </div>
            )))}
          </div>}

          <div className={classes.footer}>
            <span className={classes.footerText}>Viewing as: {selectedUser?.name}</span>
            <div className={classes.actions}>
              <ThemedButton
                isSmall={true}
                onClick={onClose}
                title={'Cancel'}
                width={90}
                buttonStyle='secondary'
              />
              <ThemedButton
                isSmall={true}
                onClick={onApplyClick}
                title={'Apply'}
                width={90}
                buttonStyle='primary'
              />
            </div>
          </div>
        </div>
      </div>
    </OverlayModal>
  , modalRoot);
};

export default SwitchUserModal;
